import { useMemo } from 'react';
// eslint-disable-next-line you-dont-need-lodash-underscore/get
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';

import { renderMd } from '../../../helpers/render-md';

type TPropPrimitive = string | number | boolean;

export interface IPropsOverriderMap {
    [path: string]: TPropPrimitive | IPropsOverriderMap;
}

export type TPropsOverrider<DataType> = {
    data: DataType;
    propsMap: IPropsOverriderMap;
};

type TGetTemplateValueProps<DataType> = {
    data: DataType;
    mapValue: TPropPrimitive | IPropsOverriderMap;
};

const lt = '[['; // левый индикатор шаблонной строки
const rt = ']]'; // правый индиактор шаблонной строки, например [[offerGroups[1].description]]
/**
 * Шаблонная строка для проброса всей data в свойство.
 * Используется в следующих кейсах:
 * - Если data не объект, а примитив - string | number | boolean
 *   Например, при переборе в SmePartnersCommonDataIterator массива строк. В propsOverrider попадает строка из перебора в качестве data.
 * - Если структура data совпадает со структурой свойства дочернего компонента, можно сразу прокинуть объект из данных, как свойство
 *   Например в data массив строк и свойство компонента ожидает массив строк.
 * - Для дебага, если нужно вывести какой-то срез данных, чтобы посмотреть, что там.
 * */
const retrieveData = '__data__';

function isTemplateString(str: unknown) {
    return typeof str === 'string' && str.indexOf(lt) > -1 && str.indexOf(rt) > 0;
}

function getPath(str: string) {
    return str.substring(str.indexOf(lt) + 2, str.indexOf(rt));
}

function getTemplateValue<DataType>({ data, mapValue }: TGetTemplateValueProps<DataType>) {
    const path = getPath(String(mapValue));
    const found = get(data, path);

    if (found) {
        return String(mapValue).replace(lt + path + rt, get(data, path));
    }
    if (path === retrieveData) {
        return typeof data === 'string'
            ? String(mapValue).replace(lt + retrieveData + rt, data)
            : data;
    }

    return found; // not found. Empty value
}

/**
 * Функция переопределения объекта свойств
 * Подменяет шаблонные вставки пути до свойства в объекте данных data
 * Поддерживает вложенные объекты свойств.
 * Примеры в /__tests__/use-props-overrider.test.ts
 * */
export function propsOverrider<DataType>({
    data,
    propsMap,
}: TPropsOverrider<DataType>): IPropsOverriderMap {
    return Object.entries(propsMap).reduce((res, [propKey, mapValue]) => {
        let value;
        if (isPlainObject(mapValue)) {
            value = propsOverrider({ data, propsMap: mapValue as IPropsOverriderMap });
        } else if (isTemplateString(mapValue)) {
            value = getTemplateValue({ data, mapValue });
        } else {
            value = mapValue;
        }

        // пропускать 0 и пустую строку и boolean
        return [undefined, null].includes(value) ? res : { ...res, [propKey]: renderMd(value) };
    }, {});
}

export const usePropsOverrider = <DataType>({ data, propsMap }: TPropsOverrider<DataType>) => {
    return useMemo(() => propsOverrider({ data, propsMap }), [data, propsMap]);
};
